import React, { useMemo } from 'react';
import { Card, Col, Empty, Row, Space, Tag, Typography } from 'antd';
import type {
  RouteViewModelCarrierFinance,
  RouteViewModelControlTask,
  RouteViewModelDocuments,
  RouteViewModelFinance,
  RouteViewModelObligationRow,
} from '../../../types/routeViewModel';
import type { Route } from '../../../types';
import { MoneyCell } from '../../../ui';
import dayjs from 'dayjs';

const { Text } = Typography;

function formatRub(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `${Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ₽`;
}

function percentLine(percent: number | null | undefined, amount: number | null | undefined): string {
  if (percent == null || !Number.isFinite(Number(percent))) return '—';
  const value = Number(percent);
  const amountText = amount != null && Number.isFinite(Number(amount)) ? ` (${formatRub(amount)})` : '';
  return `${value}%${amountText}`;
}

function amountFromPercent(rate: number | null | undefined, percent: number | null | undefined): number | null {
  if (rate == null || percent == null) return null;
  const rateValue = Number(rate);
  const percentValue = Number(percent);
  if (!Number.isFinite(rateValue) || !Number.isFinite(percentValue)) return null;
  return Math.round((rateValue * percentValue) * 100) / 10000;
}

function daysLine(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const days = Math.max(0, Math.floor(Number(value)));
  if (days === 1) return '1 день';
  if (days >= 2 && days <= 4) return `${days} дня`;
  return `${days} дней`;
}

function payableStatusMeta(payable: RouteViewModelFinance['payable'] | null | undefined): { label: string; color: string } {
  if (!payable) return { label: '—', color: 'default' };
  const required = Number(payable.totalRequired || 0);
  const paid = Number(payable.totalPaid || 0);
  const remaining = Number(payable.remaining || 0);
  const overdue = Number(payable.overdue || 0);
  if (required <= 0) return { label: '—', color: 'default' };
  if (remaining <= 0.01) return { label: 'Оплачено', color: 'green' };
  if (overdue > 0.01) return { label: 'Просрочено', color: 'red' };
  if (paid > 0.01) return { label: 'Ожидается', color: 'gold' };
  return { label: 'Не оплачено', color: 'default' };
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—';
}

type PaymentStepStatus = 'missing' | 'waiting' | 'done' | 'overdue';
type TimelineStepKind = 'payment' | 'document' | 'action';

const PAYMENT_STEP_META: Record<TimelineStepKind, Record<PaymentStepStatus, { label: string; color: string }>> = {
  payment: {
    missing: { label: 'Не оплачено', color: 'default' },
    waiting: { label: 'Ожидается', color: 'gold' },
    done: { label: 'Оплачено', color: 'green' },
    overdue: { label: 'Просрочено', color: 'red' },
  },
  document: {
    missing: { label: 'Не получен', color: 'default' },
    waiting: { label: 'Ожидается', color: 'gold' },
    done: { label: 'Получен', color: 'green' },
    overdue: { label: 'Просрочено', color: 'red' },
  },
  action: {
    missing: { label: 'Ожидается', color: 'default' },
    waiting: { label: 'Ожидается', color: 'gold' },
    done: { label: 'Выполнено', color: 'green' },
    overdue: { label: 'Просрочено', color: 'red' },
  },
};

function isTaskDone(tasks: RouteViewModelControlTask[] | undefined, taskType: string): boolean {
  return Boolean(tasks?.some((task) => task.taskType === taskType && task.status === 'DONE'));
}

function findOpenTask(tasks: RouteViewModelControlTask[] | undefined, taskType: string): RouteViewModelControlTask | null {
  return tasks?.find((task) => task.taskType === taskType && task.status !== 'DONE' && task.status !== 'CANCELLED') ?? null;
}

function isTaskOverdue(task: RouteViewModelControlTask | null): boolean {
  if (!task?.dueDate) return false;
  return dayjs().isAfter(dayjs(task.dueDate), 'day');
}

function isRequirementSatisfied(documents: RouteViewModelDocuments | null | undefined, key: string): boolean {
  return Boolean(documents?.requirements?.some((requirement) => requirement.key === key && requirement.status === 'SATISFIED'));
}

function obligationPaymentStatus(obligation: RouteViewModelObligationRow | null): PaymentStepStatus {
  if (!obligation?.triggerDate) return 'missing';
  if (obligation.timeState === 'OVERDUE' || obligation.daysOverdue > 0) return 'overdue';
  if (obligation.timeState === 'PAID' || Number(obligation.remainingAmount ?? 0) <= 0.01) return 'done';
  return 'waiting';
}

const StepBadge: React.FC<{ label: string; status: PaymentStepStatus; kind: TimelineStepKind }> = ({ label, status, kind }) => {
  const meta = PAYMENT_STEP_META[kind][status];
  return (
    <Space size={6} style={{ minWidth: 150 }}>
      <Text>{label}</Text>
      <Tag color={meta.color}>{meta.label}</Tag>
    </Space>
  );
};

type TimelineStep = {
  label: string;
  status: PaymentStepStatus;
  kind: TimelineStepKind;
};

export interface RouteFinanceTabProps {
  finance: RouteViewModelFinance | null;
  carrierFinance?: RouteViewModelCarrierFinance | null;
  documents?: RouteViewModelDocuments | null;
  tasks?: RouteViewModelControlTask[];
  isCompanyRoute: boolean;
  route: Route;
}

const RouteFinanceTab: React.FC<RouteFinanceTabProps> = ({
  finance,
  carrierFinance,
  documents,
  tasks,
  isCompanyRoute
}) => {
  const prepaymentAmount = useMemo(
    () => amountFromPercent(carrierFinance?.rateAmount, carrierFinance?.prepaymentPercent),
    [carrierFinance?.prepaymentPercent, carrierFinance?.rateAmount]
  );
  const mainPaymentAmount = useMemo(
    () => amountFromPercent(carrierFinance?.rateAmount, carrierFinance?.mainPaymentPercent),
    [carrierFinance?.mainPaymentPercent, carrierFinance?.rateAmount]
  );
  const payable = finance?.payable ?? null;
  const status = payableStatusMeta(payable);
  const prepaymentObligation = finance?.byStage.prepayment.items[0] ?? null;
  const finalObligation = finance?.byStage.final.items[0] ?? null;
  const prepaymentTriggered = Boolean(prepaymentObligation?.triggerDate);
  const finalTriggered = Boolean(finalObligation?.triggerDate);
  const prepaymentStartDate = prepaymentTriggered ? prepaymentObligation?.triggerDate ?? null : null;
  const finalStartDate = finalTriggered ? finalObligation?.triggerDate ?? null : null;
  const prepaymentPaymentDate = prepaymentTriggered ? prepaymentObligation?.dueDate ?? null : null;
  const finalPaymentDate = finalTriggered ? finalObligation?.dueDate ?? null : null;
  const carrierInvoiceUploaded = isRequirementSatisfied(documents, 'CARRIER_INVOICE');
  const carrierInvoiceConfirmed = isTaskDone(tasks, 'CARRIER_INVOICE_CONFIRM');
  const carrierInvoiceConfirmTask = findOpenTask(tasks, 'CARRIER_INVOICE_CONFIRM');
  const originalTnUploaded = isRequirementSatisfied(documents, 'ORIGINAL_TN');
  const originalTnTask = findOpenTask(tasks, 'TN_ORIGINAL_MISSING') ?? findOpenTask(tasks, 'UPLOAD_TN_ORIGINAL');
  const prepaymentPaymentStatus = obligationPaymentStatus(prepaymentObligation);
  const finalPaymentStatus = obligationPaymentStatus(finalObligation);
  const invoiceStepStatus: PaymentStepStatus = carrierInvoiceUploaded ? 'done' : 'missing';
  const confirmationStepStatus: PaymentStepStatus = carrierInvoiceConfirmed
    ? 'done'
    : isTaskOverdue(carrierInvoiceConfirmTask)
      ? 'overdue'
      : carrierInvoiceUploaded
        ? 'waiting'
        : 'missing';
  const originalTnStepStatus: PaymentStepStatus = !originalTnUploaded
    ? isTaskOverdue(originalTnTask)
      ? 'overdue'
      : 'missing'
    : finalPaymentStatus === 'done'
      ? 'done'
      : 'waiting';
  const prepaymentPercentValue = Number(carrierFinance?.prepaymentPercent ?? 0);
  const mainPaymentPercentValue = Number(carrierFinance?.mainPaymentPercent ?? 0);
  const hasPrepayment = Number.isFinite(prepaymentPercentValue) && prepaymentPercentValue > 0;
  const hasFinalPayment = Number.isFinite(mainPaymentPercentValue) && mainPaymentPercentValue > 0;
  const isSinglePrepayment = hasPrepayment && prepaymentPercentValue >= 100;
  const isSingleFinalPayment = !hasPrepayment && hasFinalPayment;
  const timelineSteps = useMemo<TimelineStep[]>(() => {
    const steps: TimelineStep[] = [
      { label: 'Счёт', status: invoiceStepStatus, kind: 'document' },
      { label: 'Подтвержден', status: confirmationStepStatus, kind: 'action' },
    ];

    if (isSinglePrepayment) {
      steps.push({ label: 'Оплата', status: prepaymentPaymentStatus, kind: 'payment' });
      return steps;
    }

    if (hasPrepayment) {
      steps.push({ label: 'Предоплата', status: prepaymentPaymentStatus, kind: 'payment' });
    }

    if (hasFinalPayment) {
      steps.push({ label: 'Оригиналы ТН', status: originalTnStepStatus, kind: 'document' });
      steps.push({ label: isSingleFinalPayment ? 'Оплата' : 'Доплата', status: finalPaymentStatus, kind: 'payment' });
    }

    return steps;
  }, [
    confirmationStepStatus,
    finalPaymentStatus,
    hasFinalPayment,
    hasPrepayment,
    invoiceStepStatus,
    isSingleFinalPayment,
    isSinglePrepayment,
    originalTnStepStatus,
    prepaymentPaymentStatus,
  ]);

  if (!carrierFinance && !finance) {
    return (
      <Card size="small" title="Финансы">
        <Empty description="Финансовые данные отсутствуют" />
      </Card>
    );
  }

  return (
    <Card size="small" title="Финансы маршрута">
      <Space direction="vertical" size={18} style={{ width: '100%' }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>Этапы</Text>
          <Space size={12} wrap>
            {timelineSteps.map((step) => (
              <StepBadge key={step.label} label={step.label} status={step.status} kind={step.kind} />
            ))}
          </Space>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>Условия оплаты</Text>
          <Row gutter={[16, 16]}>
            {hasPrepayment ? (
              <Col xs={24} md={12}>
                <Card size="small">
                  <Text type="secondary">{isSinglePrepayment ? 'Оплата' : 'Предоплата'}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Text strong>{percentLine(carrierFinance?.prepaymentPercent, prepaymentAmount)}</Text>
                  </div>
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Отсрочка: {daysLine(carrierFinance?.prepaymentDelayBankDays)}
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Начало отсчёта: {formatDate(prepaymentStartDate)}
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                    Дата оплаты: {formatDate(prepaymentPaymentDate)}
                  </Text>
                </Card>
              </Col>
            ) : null}
            {hasFinalPayment && !isSinglePrepayment ? (
              <Col xs={24} md={12}>
                <Card size="small">
                  <Text type="secondary">{isSingleFinalPayment ? 'Оплата' : 'Доплата'}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Text strong>{percentLine(carrierFinance?.mainPaymentPercent, mainPaymentAmount)}</Text>
                  </div>
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Отсрочка: {daysLine(carrierFinance?.mainPaymentDelayBankDays)}
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                    Начало отсчёта: {formatDate(finalStartDate)}
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                    Дата оплаты: {formatDate(finalPaymentDate)}
                  </Text>
                </Card>
              </Col>
            ) : null}
          </Row>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>Статус оплаты</Text>
        {isCompanyRoute ? (
          <Tag color="green">Свой маршрут</Tag>
        ) : (
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div>
                <Text type="secondary">Статус</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color={status.color}>{status.label}</Tag>
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div>
                <Text type="secondary">Оплачено / требуется</Text>
                <div style={{ marginTop: 8 }}>
                  <Text strong>
                    <MoneyCell value={Number(payable?.totalPaid ?? 0)} type="expense" /> /{' '}
                    <MoneyCell value={Number(payable?.totalRequired ?? 0)} type="expense" />
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div>
                <Text type="secondary">Остаток</Text>
                <div style={{ marginTop: 8 }}>
                  <Text strong>
                    <MoneyCell value={Number(payable?.remaining ?? 0)} type="expense" />
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        )}
        </div>
      </Space>
    </Card>
  );
};

export default RouteFinanceTab;
